import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './header';

const CreateStorePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Start with loading to check auth/store
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [error, setError] = useState(null);
  
  // Define supported cities in a central location
  const supportedCities = ['Kelowna', 'Vancouver'];
  
  // Add step state for the onboarding flow
  const [step, setStep] = useState(1);
  const totalSteps = 5;
  
  // Extended form data to include all the new fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city: '', // No default city - user must select one
    businessType: '',
    hasPhysicalLocation: null,
    address: '',
    website: '',
    contactEmail: '',
    phoneNumber: '',
    businessHours: {
      monday: { open: '09:00', close: '17:00', isClosed: false },
      tuesday: { open: '09:00', close: '17:00', isClosed: false },
      wednesday: { open: '09:00', close: '17:00', isClosed: false },
      thursday: { open: '09:00', close: '17:00', isClosed: false },
      friday: { open: '09:00', close: '17:00', isClosed: false },
      saturday: { open: '10:00', close: '15:00', isClosed: false },
      sunday: { open: '10:00', close: '15:00', isClosed: true },
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      whatsapp: ''
    },
    categories: [],
    profileImage: null
  });

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      // Decode the JWT payload
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Check if the token has an expiration time
      if (!payload.exp) return false;
      
      // Check if token is expired (exp is in seconds)
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      console.error('Error checking token expiration:', e);
      return true; // If we can't verify, assume it's expired
    }
  };

  // Check authentication and if user already has a store
  useEffect(() => {
    const checkUserAndStore = async () => {
      const token = localStorage.getItem('token');
      
      // Check if user is authenticated
      if (!token || isTokenExpired(token)) {
        console.log('User not authenticated or token expired, redirecting to login');
        navigate('/login');
        return;
      }
      
      try {
        // Check if user already has a store
        const response = await fetch('http://localhost:5000/api/user/store', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.status === 200) {
          // User already has a store, redirect to dashboard
          console.log('User already has a store, redirecting to dashboard');
          navigate('/dashboard');
          return;
        }
        
        // If we get a 404, that means user doesn't have a store yet, which is what we want
        if (response.status !== 404) {
          // Any other error status
          if (response.status === 401 || response.status === 403) {
            // Authentication error
            localStorage.removeItem('token');
            navigate('/login');
            return;
          }
          
          // Otherwise log the error but continue
          console.error('Unexpected error checking for existing store:', response.statusText);
        }
        
        // If we reach here, user is authenticated and doesn't have a store
        setInitialCheckDone(true);
        setLoading(false);
      } catch (err) {
        console.error('Error during initial check:', err);
        setError('Error checking your account. Please try again.');
        setInitialCheckDone(true);
        setLoading(false);
      }
    };
    
    checkUserAndStore();
  }, [navigate]);

  // Generate URL-friendly name from store name
  const generateUrlName = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .trim();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle category selection
  const handleCategoryToggle = (category) => {
    const updatedCategories = [...formData.categories];
    
    if (updatedCategories.includes(category)) {
      const index = updatedCategories.indexOf(category);
      updatedCategories.splice(index, 1);
    } else {
      updatedCategories.push(category);
    }
    
    setFormData({ ...formData, categories: updatedCategories });
  };

  // Handle physical/online location selection
  const handleLocationTypeChange = (hasPhysical) => {
    setFormData({ 
      ...formData, 
      hasPhysicalLocation: hasPhysical,
      // Clear address if online only
      address: hasPhysical ? formData.address : ''
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, profileImage: e.target.files[0] });
    }
  };

  // Handle social media input
  const handleSocialMediaChange = (platform, value) => {
    setFormData({
      ...formData,
      socialMedia: { ...formData.socialMedia, [platform]: value }
    });
  };

  // Handle business hours changes
  const handleHoursChange = (day, field, value) => {
    const updatedHours = { ...formData.businessHours };
    updatedHours[day][field] = value;
    setFormData({ ...formData, businessHours: updatedHours });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate that a city is selected
    if (!formData.city) {
      setError('Please select a city for your store');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const urlName = generateUrlName(formData.name);
      
      // Prepare the store data
      const storeData = {
        name: formData.name,
        description: formData.description,
        city: formData.city,
        url_name: urlName,
        has_physical_location: formData.hasPhysicalLocation || false,
        address: formData.address || '',
        website: formData.website || '',
        contact_email: formData.contactEmail || '',
        phone_number: formData.phoneNumber || '',
        business_hours: formData.businessHours || {},
        social_media: formData.socialMedia || {},
        business_type: formData.businessType || '',
        categories: formData.categories || []
      };

      // Send data to the server
      const response = await fetch('http://localhost:5000/api/stores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(storeData)
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create store');
      }
      
      const storeResponse = await response.json();
      
      // If we have a profile image, upload it
      if (formData.profileImage) {
        const imageFormData = new FormData();
        imageFormData.append('logo', formData.profileImage);
        
        const imageResponse = await fetch(`http://localhost:5000/api/stores/${storeResponse.id}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: imageFormData
        });
        
        if (!imageResponse.ok) {
          console.warn('Logo upload failed, but store was created');
        }
      }

      // Redirect to dashboard on success
      navigate(`/${formData.city.toLowerCase()}/${urlName}`);
    } catch (err) {
      console.error('Error creating store:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render progress indicator
  const StepIndicator = () => {
    const progress = (step / totalSteps) * 100;
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-2">
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <div 
              key={idx} 
              className={`rounded-circle d-flex align-items-center justify-content-center ${
                idx + 1 <= step ? 'bg-primary text-white' : 'bg-light'
              }`}
              style={{ width: '40px', height: '40px' }}
            >
              {idx + 1 <= step ? (
                <i className="bi bi-check"></i>
              ) : (
                idx + 1
              )}
            </div>
          ))}
        </div>
        <div className="progress">
          <div 
            className="progress-bar bg-primary" 
            role="progressbar" 
            style={{ width: `${progress}%` }}
            aria-valuenow={progress} 
            aria-valuemin="0" 
            aria-valuemax="100"
          />
        </div>
      </div>
    );
  };

  // Render the current step content
  const renderStepContent = () => {
    switch(step) {
      case 1:
        return (
          <div>
            <h4 className="mb-3">Let's Get Started</h4>
            
            {/* Business Type Selection */}
            <div className="mb-4">
              <label className="form-label">Business Type</label>
              <div className="row row-cols-2 g-2 mb-3">
                {['Retail Store', 'Restaurant', 'Service Business', 'Digital Storefront'].map(type => (
                  <div key={type} className="col">
                    <div 
                      className={`card h-100 ${formData.businessType === type ? 'border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setFormData({ ...formData, businessType: type })}
                    >
                      <div className="card-body text-center py-4">
                        <i className="bi bi-shop fs-3 mb-2"></i>
                        <p className="mb-0">{type}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Basic Store Information */}
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Store Name*</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your store name"
                required
              />
              <div className="form-text">
                Your store URL will be: {formData.name && formData.city 
                  ? `${formData.city.toLowerCase()}/${generateUrlName(formData.name)}` 
                  : 'Please select a city and enter a store name'}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="city" className="form-label">City*</label>
              <select
                className="form-control"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a city</option>
                {supportedCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <div className="form-text text-muted">
                Select your store location
              </div>
            </div>
            
            {/* Location Type */}
            <div className="mb-3">
              <label className="form-label">Business Location</label>
              <div className="d-flex gap-2">
                <button 
                  type="button"
                  className={`btn flex-grow-1 ${formData.hasPhysicalLocation === true ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleLocationTypeChange(true)}
                >
                  <i className="bi bi-geo-alt me-2"></i>
                  Physical Location
                </button>
                <button 
                  type="button"
                  className={`btn flex-grow-1 ${formData.hasPhysicalLocation === false ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleLocationTypeChange(false)}
                >
                  <i className="bi bi-globe me-2"></i>
                  Digital Storefront
                </button>
              </div>
            </div>
            
            {/* Address Field (conditional) */}
            {formData.hasPhysicalLocation && (
              <div className="mb-3">
                <label htmlFor="address" className="form-label">Store Address</label>
                <input
                  type="text"
                  className="form-control"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={formData.city ? `123 Main St, ${formData.city}, BC` : "Please select a city first"}
                />
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div>
            <h4 className="mb-3">Business Details</h4>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Store Description*</label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell customers what makes your business special..."
                required
              />
            </div>
            
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label htmlFor="contactEmail" className="form-label">Contact Email</label>
                <input
                  type="email"
                  className="form-control"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="contact@yourbusiness.com"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-control"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(250) 555-1234"
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label className="form-label">Business Categories (Select all that apply)</label>
              <div className="row row-cols-3 g-2">
                {['Food', 'Fashion', 'Art', 'Home', 'Technology', 'Health', 'Beauty', 'Sports', 'Books'].map(category => (
                  <div key={category} className="col">
                    <div 
                      className={`card ${formData.categories.includes(category) ? 'border-primary bg-light' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleCategoryToggle(category)}
                    >
                      <div className="card-body py-2 text-center">
                        <small>{category}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div>
            <h4 className="mb-3">Store Presence</h4>
            
            {/* Profile Image */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Profile Image</h5>
                <div className="text-center p-4 border rounded mt-3">
                  {formData.profileImage ? (
                    <div>
                      <img 
                        src={URL.createObjectURL(formData.profileImage)} 
                        alt="Store preview" 
                        className="img-fluid mb-3"
                        style={{ maxHeight: '200px', objectFit: 'contain' }} 
                      />
                      <p>{formData.profileImage.name}</p>
                      <button 
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setFormData({ ...formData, profileImage: null })}
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <div>
                      <i className="bi bi-camera fs-1 text-secondary mb-3"></i>
                      <p className="text-muted mb-3">Upload a store logo or profile image</p>
                      <input
                        type="file"
                        id="profileImage"
                        className="d-none"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      <button 
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => document.getElementById('profileImage').click()}
                      >
                        Select Image
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Online Presence */}
            <h5 className="mb-3">Online Presence</h5>
            <div className="mb-3">
              <label htmlFor="website" className="form-label">Website</label>
              <input
                type="url"
                className="form-control"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourbusiness.com"
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="instagram" className="form-label">Instagram</label>
              <div className="input-group">
                <span className="input-group-text">@</span>
                <input
                  type="text"
                  className="form-control"
                  id="instagram"
                  placeholder="yourbusiness"
                  value={formData.socialMedia.instagram}
                  onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="facebook" className="form-label">Facebook</label>
              <input
                type="text"
                className="form-control"
                id="facebook"
                placeholder="facebook.com/yourbusiness"
                value={formData.socialMedia.facebook}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="whatsapp" className="form-label">WhatsApp</label>
              <input
                type="text"
                className="form-control"
                id="whatsapp"
                placeholder="+1 (250) 555-1234"
                value={formData.socialMedia.whatsapp}
                onChange={(e) => handleSocialMediaChange('whatsapp', e.target.value)}
              />
            </div>
          </div>
        );
        
      case 4:
        return (
          <div>
            <h4 className="mb-3">Business Hours</h4>
            <div className="card">
              <div className="card-body">
                {Object.keys(formData.businessHours).map((day) => (
                  <div key={day} className="mb-3 pb-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-capitalize fw-bold">{day}</span>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`${day}-open`}
                          checked={!formData.businessHours[day].isClosed}
                          onChange={() => {
                            handleHoursChange(day, 'isClosed', !formData.businessHours[day].isClosed);
                          }}
                        />
                        <label className="form-check-label" htmlFor={`${day}-open`}>
                          {formData.businessHours[day].isClosed ? 'Closed' : 'Open'}
                        </label>
                      </div>
                    </div>
                    
                    {!formData.businessHours[day].isClosed && (
                      <div className="row g-3">
                        <div className="col-6">
                          <label htmlFor={`${day}-open-time`} className="form-label">Open</label>
                          <input
                            type="time"
                            className="form-control"
                            id={`${day}-open-time`}
                            value={formData.businessHours[day].open}
                            onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                          />
                        </div>
                        <div className="col-6">
                          <label htmlFor={`${day}-close-time`} className="form-label">Close</label>
                          <input
                            type="time"
                            className="form-control"
                            id={`${day}-close-time`}
                            value={formData.businessHours[day].close}
                            onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div>
            <div className="text-center mb-4">
              <div className="d-inline-block rounded-circle bg-success p-3 mb-3">
                <i className="bi bi-check-lg text-white fs-1"></i>
              </div>
              <h4>Your store is ready to launch!</h4>
              <p className="text-muted">Review your store details below and click Launch to make your store visible.</p>
            </div>
            
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Store Summary</h5>
                <div className="row g-0 py-2 border-bottom">
                  <div className="col-4 fw-bold">Store Name:</div>
                  <div className="col-8">{formData.name}</div>
                </div>
                <div className="row g-0 py-2 border-bottom">
                  <div className="col-4 fw-bold">Business Type:</div>
                  <div className="col-8">{formData.businessType || 'Not specified'}</div>
                </div>
                <div className="row g-0 py-2 border-bottom">
                  <div className="col-4 fw-bold">Location:</div>
                  <div className="col-8">{formData.city}</div>
                </div>
                {formData.hasPhysicalLocation && (
                  <div className="row g-0 py-2 border-bottom">
                    <div className="col-4 fw-bold">Address:</div>
                    <div className="col-8">{formData.address || 'Not specified'}</div>
                  </div>
                )}
                <div className="row g-0 py-2 border-bottom">
                  <div className="col-4 fw-bold">Categories:</div>
                  <div className="col-8">
                    {formData.categories.length > 0 
                      ? formData.categories.join(', ') 
                      : 'None selected'}
                  </div>
                </div>
                <div className="row g-0 py-2 border-bottom">
                  <div className="col-4 fw-bold">Contact:</div>
                  <div className="col-8">
                    {formData.contactEmail || formData.phoneNumber 
                      ? <>{formData.contactEmail} {formData.phoneNumber && `• ${formData.phoneNumber}`}</>
                      : 'Not specified'}
                  </div>
                </div>
                <div className="row g-0 py-2">
                  <div className="col-4 fw-bold">URL:</div>
                  <div className="col-8">
                    {formData.name && formData.city
                      ? `storetoyou.ca/${formData.city.toLowerCase()}/${generateUrlName(formData.name)}` 
                      : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  // Render loading state while performing initial authentication/store check
  if (!initialCheckDone || (loading && !formData.name)) {
    return (
      <>
        <Header />
        <div className="container-fluid bg-light py-5">
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Checking your account...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container-fluid bg-light py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card shadow">
                <div className="card-body">
                  <h2 className="card-title mb-4">Create Your Store</h2>
                  
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p>Creating your store...</p>
                    </div>
                  ) : (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      if (step === totalSteps) {
                        handleSubmit();
                      } else {
                        setStep(step + 1);
                      }
                    }}>
                      <StepIndicator />
                      
                      {renderStepContent()}
                      
                      <div className="d-flex justify-content-between mt-4">
                        {step > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setStep(step - 1)}
                          >
                            Back
                          </button>
                        )}
                        
                        {step === 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </button>
                        )}
                        
                        <button
                          type="submit"
                          className={`btn ${step === totalSteps ? 'btn-success' : 'btn-primary'} ms-auto`}
                        >
                          {step === totalSteps ? 'Launch Store' : 'Continue'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateStorePage;